/**
 * InstallSourcesCard
 * ──────────────────
 * Admin-dashboard tile that shows which install surface is driving
 * clicks over a configurable window (default: last 7 days). Powered by
 * /api/admin/install/summary which returns a source × store rollup.
 *
 * Data source is the app's OWN click-tracking (`install_clicks`
 * Mongo collection) rather than App Store Connect / Play Console
 * APIs — so it reports real-time click intent, not delayed install
 * conversion. Attribution UTM tags are already appended to the CTA
 * URLs by useInstallPrompt.js, so if you later plug in the App Store
 * / Play Console integrations you can cross-reference click → install
 * ratios per source.
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, ExternalLink, Loader2 } from 'lucide-react';
import { API_URL as API } from '@/utils/apiConfig';

const SOURCE_LABELS = {
  'landing-hero': 'Landing hero',
  'landing-banner': 'Landing banner',
  'home-badges': 'Home badges',
  'reflection-banner': 'Reflection banner',
  'community-banner': 'Community banner',
  unknown: 'Unknown',
};

export default function InstallSourcesCard({ colors, isDark }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/admin/install/summary`, { params: { days }, withCredentials: true })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ total: 0, by_source: {}, by_store: {} }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const bg = isDark ? '#1e293b' : '#ffffff';
  const border = isDark ? '#334155' : '#e5e7eb';

  return (
    <div
      className="rounded-2xl p-5 shadow-sm"
      style={{ background: bg, border: `1px solid ${border}` }}
      data-testid="admin-install-sources-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div
            className="p-2 rounded-lg"
            style={{ background: 'rgba(99, 102, 241, 0.12)' }}
          >
            <Download className="h-4 w-4" style={{ color: '#6366f1' }} />
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: colors.text }}>
              Install-CTA Clicks
            </h3>
            <p className="text-[11px]" style={{ color: colors.textMuted }}>
              Where users tap &ldquo;Get the app&rdquo;
            </p>
          </div>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="text-xs rounded-md px-2 py-1"
          style={{
            background: 'transparent',
            border: `1px solid ${border}`,
            color: colors.textSecondary,
          }}
          data-testid="install-sources-range"
        >
          <option value={1}>Last 24h</option>
          <option value={7}>Last 7d</option>
          <option value={30}>Last 30d</option>
          <option value={90}>Last 90d</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-4 w-4 animate-spin" style={{ color: colors.textMuted }} />
        </div>
      ) : (
        <>
          <div className="flex items-baseline gap-3 mb-4">
            <span className="text-2xl font-bold" style={{ color: colors.text }}>
              {data?.total ?? 0}
            </span>
            <span className="text-xs" style={{ color: colors.textMuted }}>
              total clicks
            </span>
            {data?.by_store && (
              <span className="text-xs" style={{ color: colors.textMuted }}>
                · {data.by_store['app-store'] || 0} iOS · {data.by_store['play-store'] || 0} Android
              </span>
            )}
          </div>

          <div className="space-y-2">
            {Object.entries(data?.by_source || {})
              .sort(([, a], [, b]) => b - a)
              .map(([src, count]) => {
                const pct = data.total > 0 ? Math.round((count / data.total) * 100) : 0;
                return (
                  <div key={src} className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium truncate" style={{ color: colors.text }}>
                          {SOURCE_LABELS[src] || src}
                        </span>
                        <span className="text-xs tabular-nums" style={{ color: colors.textSecondary }}>
                          {count} <span style={{ color: colors.textMuted }}>· {pct}%</span>
                        </span>
                      </div>
                      <div
                        className="h-1.5 rounded-full overflow-hidden"
                        style={{ background: isDark ? '#0f172a' : '#f1f5f9' }}
                      >
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${pct}%`,
                            background: 'linear-gradient(90deg, #6366f1, #8b5cf6)',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            {(!data?.by_source || Object.keys(data.by_source).length === 0) && (
              <p className="text-xs py-2" style={{ color: colors.textMuted }}>
                No install clicks yet. Data will appear once users tap a store CTA.
              </p>
            )}
          </div>

          <p className="mt-4 text-[10px] flex items-center gap-1" style={{ color: colors.textMuted }}>
            <ExternalLink className="h-3 w-3" />
            Server-side click tracking · attribution UTMs surface in App Store Connect + Play Console
          </p>
        </>
      )}
    </div>
  );
}
